import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, FileText, PenLine, Camera, CheckCircle2, ReceiptText, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAdmin } from "@/admin/AdminContext";
import { api } from "@/admin/api";
import { STATUS, fmtPrice } from "@/data/devisCatalog";
import SignaturePad from "@/components/admin/SignaturePad";
import SharePdfButton from "@/components/admin/SharePdfButton";

const fmtDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
};

const AdminDevisDetail = () => {
    const { admin } = useAdmin();
    const navigate = useNavigate();
    const { id } = useParams();
    const [devis, setDevis] = useState(null);
    const [error, setError] = useState("");
    const [signing, setSigning] = useState(false);
    const [signerName, setSignerName] = useState("");
    const [signature, setSignature] = useState(null);
    const [completing, setCompleting] = useState(false);
    const [paymentMode, setPaymentMode] = useState("Carte bancaire (TPE)");
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const load = () => {
        api.getDevis(id)
            .then((d) => {
                setDevis(d);
                setSignerName(d.signer_name || d.client?.name || "");
            })
            .catch((e) => setError(e.message));
    };

    useEffect(() => {
        if (admin === null) {
            navigate("/admin", { replace: true });
            return;
        }
        if (admin) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [admin, id, navigate]);

    if (!admin) return null;
    if (error) return <div className="flex min-h-screen items-center justify-center bg-mist px-5"><p data-testid="admin-detail-error" className="rounded-2xl bg-red-50 px-5 py-4 text-sm font-semibold text-red-700">{error}</p></div>;
    if (!devis) return <div className="flex min-h-screen items-center justify-center bg-mist"><p className="text-sm text-slate-500">Chargement...</p></div>;

    const st = STATUS[devis.status] || STATUS.brouillon;
    const photosCount = (devis.photos || []).length;

    // Messages préremplis qui accompagnent le PDF dans le partage (WhatsApp, Mail, SMS...)
    const devisShareText = `Bonjour ${devis.client?.name}, voici votre devis ${devis.number} Eradikbreizh d'un montant de ${fmtPrice(devis.total_ht)} HT (TVA non applicable). Intervention prévue le ${fmtDate(devis.intervention_date)}. Ce devis est valable 30 jours. Bonne journée ! Eradikbreizh, 02 55 99 22 43`;
    const factureShareText = `Bonjour ${devis.client?.name}, voici votre facture ${devis.invoice_number} d'un montant de ${fmtPrice(devis.total_ht)} HT, réglée par ${devis.payment_mode} le ${fmtDate(devis.invoiced_at)}. Merci de votre confiance et à bientôt ! Eradikbreizh, 02 55 99 22 43`;

    const doSign = async () => {
        if (!signature) {
            toast.error("Faites signer le client dans le cadre");
            return;
        }
        setBusy(true);
        try {
            const updated = await api.signDevis(id, { signer_name: signerName, signature });
            setDevis(updated);
            setSigning(false);
            toast.success(`Devis ${updated.number} accepté et signé`);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    const doUpload = async (files) => {
        if (!files?.length) return;
        setBusy(true);
        try {
            const updated = await api.uploadPhotos(id, files);
            setDevis(updated);
            toast.success(`${files.length} photo(s) ajoutée(s)`);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const doComplete = async () => {
        setBusy(true);
        try {
            const updated = await api.completeDevis(id, { payment_mode: paymentMode });
            setDevis(updated);
            setCompleting(false);
            toast.success(`Facture ${updated.invoice_number} émise, prestation clôturée`);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    const doDelete = async () => {
        setBusy(true);
        try {
            const res = await api.deleteDevis(id);
            toast.success(`Devis ${res.number} supprimé`);
            navigate("/admin/dashboard", { replace: true });
        } catch (e) {
            toast.error(e.message);
            setBusy(false);
            setConfirmingDelete(false);
        }
    };

    return (
        <div data-testid="admin-devis-detail" className="min-h-screen bg-mist pb-32">
            <header className="sticky top-0 z-40 border-b border-black/10 bg-white">
                <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-3">
                    <Link to="/admin/dashboard" data-testid="admin-detail-back" className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-brand hover:text-brand">
                        <ArrowLeft className="h-4 w-4" />
                        Devis
                    </Link>
                    <span className={`rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide ${st.classes}`} data-testid="detail-status">{st.label}</span>
                </div>
            </header>

            <main className="mx-auto max-w-3xl space-y-5 px-5 pt-6">
                {/* RÉCAP */}
                <section className="rounded-3xl border border-black/10 bg-white p-5">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h1 className="font-display text-2xl font-black text-night" data-testid="detail-number">
                                {devis.status === "facture" ? devis.invoice_number : devis.number}
                            </h1>
                            <p className="mt-1 text-xs text-slate-500">Créé le {fmtDate(devis.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            {devis.status === "brouillon" && (
                                <Link to={`/admin/devis/${devis.id}/modifier`} data-testid="detail-edit-link" className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-brand hover:text-brand">
                                    <Pencil className="h-4 w-4" />
                                    Modifier
                                </Link>
                            )}
                            {devis.status !== "facture" && (
                                confirmingDelete ? (
                                    <div className="flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1.5">
                                        <button
                                            data-testid="detail-delete-confirm"
                                            onClick={doDelete}
                                            disabled={busy}
                                            className="text-sm font-bold text-red-600 disabled:opacity-50"
                                        >
                                            {busy ? "Suppression..." : "Oui, supprimer"}
                                        </button>
                                        <span className="text-red-300">|</span>
                                        <button data-testid="detail-delete-cancel" onClick={() => setConfirmingDelete(false)} className="text-sm font-semibold text-slate-600">Non</button>
                                    </div>
                                ) : (
                                    <button
                                        data-testid="detail-delete-button"
                                        onClick={() => setConfirmingDelete(true)}
                                        aria-label="Supprimer le devis"
                                        className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-red-300 hover:text-red-600"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                    <div className="mt-4 border-t border-black/10 pt-4">
                        {devis.client?.type === "societe" && devis.client?.company && (
                            <p data-testid="detail-client-company" className="text-base font-bold text-brand">{devis.client.company}{devis.client.siret ? ` · SIRET ${devis.client.siret}` : ""}</p>
                        )}
                        <p className={`${devis.client?.type === "societe" ? "mt-0.5 text-sm font-semibold text-slate-700" : "text-base font-bold text-night"}`} data-testid="detail-client-name">{devis.client?.name}</p>
                        {(devis.client?.address || devis.client?.city) && (
                            <p className="mt-0.5 text-sm text-slate-600">{[devis.client?.address, `${devis.client?.zip || ""} ${devis.client?.city || ""}`.trim()].filter(Boolean).join(", ")}</p>
                        )}
                        {devis.client?.billing_same === false && devis.client?.billing?.address && (
                            <p data-testid="detail-billing-address" className="mt-0.5 text-xs text-slate-500">Facturation : {[devis.client.billing.address, `${devis.client.billing.zip || ""} ${devis.client.billing.city || ""}`.trim()].filter(Boolean).join(", ")}</p>
                        )}
                        <a data-testid="detail-client-phone" href={`tel:${devis.client?.phone}`} className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand/10 px-5 py-3 text-base font-bold text-brand transition-colors hover:bg-brand hover:text-white">
                            <Phone className="h-5 w-5" />
                            {devis.client?.phone}
                        </a>
                        <p className="mt-3 text-sm text-slate-600">
                            Intervention le <strong>{fmtDate(devis.intervention_date)}</strong>
                            {devis.surcharge_applied && <span className="ml-2 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">+20 % week-end/férié</span>}
                        </p>
                    </div>
                </section>

                {/* LIGNES */}
                <section className="rounded-3xl border border-black/10 bg-white p-5">
                    <h2 className="font-display text-sm font-black uppercase tracking-[0.15em] text-brand">Prestations</h2>
                    <ul className="mt-3 divide-y divide-black/5">
                        {devis.items.map((item, i) => (
                            <li key={i} className="flex items-start justify-between gap-3 py-3">
                                <div>
                                    <p className="text-sm font-semibold text-night">{item.label}</p>
                                    <p className="text-xs text-slate-500">×{item.qty} à {fmtPrice(item.unit_price)}</p>
                                </div>
                                <p className="shrink-0 text-sm font-black text-night">{fmtPrice(item.qty * item.unit_price)}</p>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-3 space-y-1.5 border-t border-black/10 pt-3 text-sm">
                        <div className="flex justify-between text-slate-600"><span>Total HT</span><span>{fmtPrice(devis.subtotal_ht)}</span></div>
                        {devis.surcharge_applied && (
                            <div className="flex justify-between text-amber-700"><span>Majoration +20 %</span><span>{fmtPrice(devis.total_ht - devis.subtotal_ht)}</span></div>
                        )}
                        <div className="flex justify-between pt-1 font-display text-lg font-black text-night" data-testid="detail-total">
                            <span>TOTAL</span><span>{fmtPrice(devis.total_ht)}</span>
                        </div>
                        <p className="text-right text-[11px] text-slate-400">TVA non applicable, art. 293 B du CGI</p>
                    </div>
                </section>

                {/* PARTAGE + PDF DEVIS */}
                <SharePdfButton
                    testid="share-devis-button"
                    url={api.pdfUrl(devis.id, "devis")}
                    filename={`devis-${devis.number}.pdf`}
                    shareTitle={`Devis ${devis.number} - Eradikbreizh`}
                    shareText={devisShareText}
                    label={devis.status === "brouillon" ? "Partager le devis (WhatsApp, mail...)" : "Partager le devis signé"}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand py-4 text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover active:scale-95 disabled:opacity-60"
                />
                <a
                    data-testid="pdf-devis-link"
                    href={api.pdfUrl(devis.id, "devis")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 rounded-2xl border-2 border-brand/30 bg-white py-3.5 text-sm font-bold text-brand transition-colors hover:bg-brand/5"
                >
                    <FileText className="h-4 w-4" />
                    {devis.status === "brouillon" ? "Voir le devis PDF (à faire signer)" : "Voir le devis signé PDF"}
                </a>

                {/* ÉTAPE SIGNATURE */}
                {devis.status === "brouillon" && (
                    <section className="rounded-3xl border border-black/10 bg-white p-5">
                        {!signing ? (
                            <button
                                data-testid="sign-open-button"
                                onClick={() => setSigning(true)}
                                className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover active:scale-95"
                            >
                                <PenLine className="h-5 w-5" />
                                Faire signer le client
                            </button>
                        ) : (
                            <div>
                                <h2 className="font-display text-sm font-black uppercase tracking-[0.15em] text-brand">Signature du client</h2>
                                <p className="mt-1 text-xs text-slate-500">Présentez le devis PDF au client, puis faites-le signer ci-dessous.</p>
                                <input
                                    data-testid="signature-name"
                                    className="mt-4 w-full rounded-2xl border border-black/15 px-5 py-4 text-base outline-none focus:border-brand"
                                    placeholder="Nom du signataire"
                                    value={signerName}
                                    onChange={(e) => setSignerName(e.target.value)}
                                />
                                <div className="mt-3">
                                    <SignaturePad onChange={setSignature} />
                                </div>
                                <div className="mt-4 flex gap-3">
                                    <button
                                        data-testid="signature-submit"
                                        onClick={doSign}
                                        disabled={busy || !signature}
                                        className="flex flex-1 items-center justify-center gap-2 rounded-full bg-brand py-4 text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover active:scale-95 disabled:opacity-50"
                                    >
                                        <CheckCircle2 className="h-5 w-5" />
                                        {busy ? "Enregistrement..." : "Devis accepté, valider"}
                                    </button>
                                    <button
                                        data-testid="sign-cancel-button"
                                        onClick={() => setSigning(false)}
                                        className="rounded-full border border-black/15 px-6 py-4 text-sm font-semibold text-slate-600"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* ÉTAPE PHOTOS + CLÔTURE */}
                {devis.status === "signe" && (
                    <section className="rounded-3xl border border-black/10 bg-white p-5">
                        <h2 className="font-display text-sm font-black uppercase tracking-[0.15em] text-brand">Photos de l'intervention</h2>
                        <p className="mt-1 text-xs text-slate-500">Au moins 1 photo obligatoire pour clôturer la prestation.</p>
                        {photosCount > 0 && (
                            <div className="mt-4 grid grid-cols-3 gap-2">
                                {devis.photos.map((p, i) => (
                                    <img key={i} data-testid={`photo-thumb-${i}`} src={`data:image/jpeg;base64,${p.data}`} alt={p.name} className="aspect-square w-full rounded-xl border border-black/10 object-cover" />
                                ))}
                            </div>
                        )}
                        <input ref={fileRef} data-testid="photos-input" type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => doUpload(e.target.files)} />
                        <button
                            data-testid="photos-add-button"
                            onClick={() => fileRef.current?.click()}
                            disabled={busy}
                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-brand/40 bg-brand/5 py-4 text-base font-bold text-brand transition-colors hover:bg-brand/10"
                        >
                            <Camera className="h-5 w-5" />
                            {busy ? "Envoi en cours..." : photosCount > 0 ? `Ajouter d'autres photos (${photosCount})` : "Prendre / choisir des photos"}
                        </button>

                        {!completing ? (
                            <button
                                data-testid="complete-button"
                                onClick={() => setCompleting(true)}
                                disabled={photosCount === 0}
                                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-green-600 py-4 text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-green-700 active:scale-95 disabled:opacity-40"
                            >
                                <CheckCircle2 className="h-5 w-5" />
                                Prestation terminée
                            </button>
                        ) : (
                            <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 p-4">
                                <p className="text-sm font-bold text-night">Mode de règlement</p>
                                <select
                                    data-testid="payment-mode-select"
                                    value={paymentMode}
                                    onChange={(e) => setPaymentMode(e.target.value)}
                                    className="mt-2 w-full rounded-2xl border border-black/15 bg-white px-5 py-4 text-base outline-none focus:border-brand"
                                >
                                    <option>Carte bancaire (TPE)</option>
                                    <option>Carte bancaire (paiement à distance)</option>
                                </select>
                                <p className="mt-2 text-xs text-slate-500">La facture acquittée {`F-${devis.seq}`} sera générée immédiatement.</p>
                                <div className="mt-3 flex gap-3">
                                    <button
                                        data-testid="complete-confirm-button"
                                        onClick={doComplete}
                                        disabled={busy}
                                        className="flex flex-1 items-center justify-center gap-2 rounded-full bg-green-600 py-4 text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-green-700 active:scale-95 disabled:opacity-60"
                                    >
                                        <ReceiptText className="h-5 w-5" />
                                        {busy ? "Clôture..." : "Encaissé, émettre la facture"}
                                    </button>
                                    <button data-testid="complete-cancel-button" onClick={() => setCompleting(false)} className="rounded-full border border-black/15 px-6 py-4 text-sm font-semibold text-slate-600">Annuler</button>
                                </div>
                            </div>
                        )}
                        {photosCount === 0 && <p data-testid="complete-hint" className="mt-2 text-center text-xs font-semibold text-amber-700">Ajoutez d'abord au moins 1 photo</p>}
                    </section>
                )}

                {/* FACTURE */}
                {devis.status === "facture" && (
                    <section className="rounded-3xl border border-green-200 bg-green-50 p-5" data-testid="invoice-done">
                        <div className="flex items-center gap-3">
                            <CheckCircle2 className="h-8 w-8 shrink-0 text-green-600" />
                            <div>
                                <p className="font-display text-base font-black text-green-800">Facture {devis.invoice_number} acquittée</p>
                                <p className="text-xs text-green-700">Payée par {devis.payment_mode} le {fmtDate(devis.invoiced_at)}</p>
                            </div>
                        </div>
                        <SharePdfButton
                            testid="share-facture-button"
                            url={api.pdfUrl(devis.id, "facture")}
                            filename={`facture-${devis.invoice_number}.pdf`}
                            shareTitle={`Facture ${devis.invoice_number} acquittée - Eradikbreizh`}
                            shareText={factureShareText}
                            label="Partager la facture (WhatsApp, mail...)"
                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-green-600 py-4 text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-green-700 active:scale-95 disabled:opacity-60"
                        />
                        <a
                            data-testid="pdf-facture-link"
                            href={api.pdfUrl(devis.id, "facture")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border-2 border-green-600/30 bg-white py-3.5 text-sm font-bold text-green-700 transition-colors hover:bg-green-100/60"
                        >
                            <FileText className="h-4 w-4" />
                            Ouvrir la facture PDF
                        </a>
                        {photosCount > 0 && (
                            <div className="mt-4 grid grid-cols-3 gap-2">
                                {devis.photos.map((p, i) => (
                                    <img key={i} data-testid={`photo-thumb-${i}`} src={`data:image/jpeg;base64,${p.data}`} alt={p.name} className="aspect-square w-full rounded-xl border border-black/10 object-cover" />
                                ))}
                            </div>
                        )}
                        {devis.signed_at && (
                            <p className="mt-3 text-xs text-green-700">Devis signé par {devis.signer_name} le {fmtDate(devis.signed_at)}</p>
                        )}
                    </section>
                )}
            </main>
        </div>
    );
};

export default AdminDevisDetail;
